import ApiClientOpen from '../api/axios/apiClientOpen';
import type { TipoProductoListResponse, TipoProductoResponse, TipoProductoRequest } from '../../types';
import qs from 'qs';
import type { StrapiFilters, StrapiSort, StrapiPopulate, StrapiPagination } from '../../types/_strapi.model';

const TIPO_PRODUCTO_ENDPOINT = '/tipo-productos';

interface GetAllTipoProductosParams {
  filters?: StrapiFilters;
  sort?: StrapiSort | string[];
  fields?: string[];
  populate?: StrapiPopulate | string[];
  pagination?: StrapiPagination;
}

interface GetAllParams {
  page: number;
  limit: number;
  filter?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Service class for TipoProducto entity operations
 */
class TipoProductoService {
  /**
   * Get all tipo-productos with optional filtering, sorting, population, and pagination
   * @param params Object containing filters, sort, fields, populate, and pagination options
   * @returns Promise with list of tipo-productos and metadata
   */
  async getAllTipoProductos(params?: GetAllTipoProductosParams): Promise<TipoProductoListResponse> {
    try {
      // Build query parameters using qs
      const queryParams = qs.stringify(params || {}, {
        encodeValuesOnly: true, // prettify URL
      });

      const queryString = queryParams ? `?${queryParams}` : '';
      const response = await ApiClientOpen.get<TipoProductoListResponse>(`${TIPO_PRODUCTO_ENDPOINT}${queryString}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tipo-productos:', error);
      throw error;
    }
  }

  /** 
   * Get all tipo-productos with pagination, filtering, and sorting
   * @param params Object containing pagination, filtering, and sorting options
   * @returns Promise with list of tipo-productos and metadata
   */
  async getAll(params: GetAllParams): Promise<TipoProductoListResponse> {
    try {
      const { page, limit, filter, sortBy, sortOrder } = params;
      
      // Build Strapi query parameters
      const strapiParams: any = {
        pagination: {
          page,
          pageSize: limit
        }
      };
      
      // Add filter if provided
      if (filter) {
        strapiParams.filters = {
          $or: [
            { nombre: { $contains: filter } },
            { descripcion: { $contains: filter } }
          ]
        }
      }
      
      // Add sorting if provided
      if (sortBy && sortOrder) {
        strapiParams.sort = `${sortBy}:${sortOrder}`;
      }
      
      // Build query parameters using qs
      const queryParams = qs.stringify(strapiParams, {
        encodeValuesOnly: true, // prettify URL
      });

      const queryString = queryParams ? `?${queryParams}` : '';
      const response = await ApiClientOpen.get<TipoProductoListResponse>(`${TIPO_PRODUCTO_ENDPOINT}${queryString}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tipo-productos:', error);
      throw error;
    }
  }

  /**
   * Get a tipo-producto by ID with optional population and field selection
   * @param id TipoProducto ID
   * @param populate Optional fields to populate
   * @param fields Optional fields to select
   * @returns Promise with tipo-producto data
   */
  async getTipoProductoById(
    id: number | string, 
    populate?: StrapiPopulate | string[], 
    fields?: string[]
  ): Promise<TipoProductoResponse> {
    try {
      const queryOptions: any = {};
      
      if (populate) {
        queryOptions.populate = populate;
      }
      
      if (fields) {
        queryOptions.fields = fields;
      }
      
      const queryParams = qs.stringify(queryOptions, {
        encodeValuesOnly: true,
      });
      
      const queryString = queryParams ? `?${queryParams}` : '';
      const response = await ApiClientOpen.get<TipoProductoResponse>(`${TIPO_PRODUCTO_ENDPOINT}/${id}${queryString}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching tipo-producto with id ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new tipo-producto
   * @param tipoProductoData TipoProducto data to create
   * @returns Promise with created tipo-producto data
   */
  async createTipoProducto(tipoProductoData: TipoProductoRequest): Promise<TipoProductoResponse> {
    try {
      const response = await ApiClientOpen.post<TipoProductoResponse>(TIPO_PRODUCTO_ENDPOINT, tipoProductoData);
      return response.data;
    } catch (error) {
      console.error('Error creating tipo-producto:', error);
      throw error;
    }
  }

  /**
   * Create a new tipo-producto (alias method)
   * @param tipoProductoData TipoProducto data to create
   * @returns Promise with created tipo-producto data
   */
  async create(tipoProductoData: TipoProductoRequest): Promise<TipoProductoResponse> {
    return this.createTipoProducto(tipoProductoData);
  }

  /**
   * Update an existing tipo-producto
   * @param id TipoProducto ID to update
   * @param tipoProductoData Updated tipo-producto data
   * @returns Promise with updated tipo-producto data
   */
  async updateTipoProducto(id: number | string, tipoProductoData: TipoProductoRequest): Promise<TipoProductoResponse> {
    try {
      const response = await ApiClientOpen.put<TipoProductoResponse>(`${TIPO_PRODUCTO_ENDPOINT}/${id}`, tipoProductoData);
      return response.data;
    } catch (error) {
      console.error(`Error updating tipo-producto with id ${id}:`, error);
      throw error;
    }
  }

  /**
   * Update an existing tipo-producto (alias method)
   * @param id TipoProducto ID to update
   * @param tipoProductoData Updated tipo-producto data
   * @returns Promise with updated tipo-producto data
   */
  async update(id: number | string, tipoProductoData: TipoProductoRequest): Promise<TipoProductoResponse> {
    return this.updateTipoProducto(id, tipoProductoData);
  }

  /**
   * Delete a tipo-producto by ID (alias method)
   * @param id TipoProducto ID to delete
   * @returns Promise indicating success or failure
   */
  async delete(id: number | string): Promise<void> {
    return this.deleteTipoProducto(id);
  }

  /**
   * Delete a tipo-producto by ID
   * @param id TipoProducto ID to delete
   * @returns Promise indicating success or failure
   */
  async deleteTipoProducto(id: number | string): Promise<void> {
    try {
      await ApiClientOpen.delete(`${TIPO_PRODUCTO_ENDPOINT}/${id}`);
    } catch (error) {
      console.error(`Error deleting tipo-producto with id ${id}:`, error);
      throw error;
    }
  }
}

export default new TipoProductoService();